import sepview7logo from "./images/others/sepview7logo.svg";

import {boundMethod} from "autobind-decorator";
import {parseISO} from "date-fns";
import React from "react";
import {IntlContext, IntlShape} from "react-intl";
import {
    BrowserRouter,
    Redirect,
    Route,
    RouteComponentProps,
    Switch,
} from "react-router-dom";
import {Titled} from "react-titled";

import session from "@/services/session";
import license, {ILicenseStatus} from "./services/license";

import ContainerCol from "@toolbox/display-blocks/ContainerCol";
import {isRecorder} from "@toolbox/functions/browser-checks";
import createRedirector from "@toolbox/functions/create-redirector";
import Provider from "@translate/Provider";
import {intl2Str} from "@translate/T";
import Footer from "./Footer";
import SessionCheck from "./SessionCheck";
import Startup from "./Startup";
import Eula from "./components/Eula";
import Home from "./components/Home";
import Login from "./components/Login";
import ThirdParty from "./components/ThirdParty";
import Copy2ClipboardModal from "./components/info-global/Copy2ClipboardModal";
import LoadingIndicator from "./components/info-global/LoadingIndicator";
import NavBar from "./components/navbar/Index";
import Warnings from "./components/warnings/Index";
import BlockWarning from "./components/warnings/hard-drice-space/BlockWarning";

import {EExpireReasons} from "./services/models";

interface IAppState {
    started: boolean;
    licenseStatus: ILicenseStatus;
}

class App extends React.PureComponent<{}, IAppState> {
    public readonly state: IAppState = {
        started: false,
        licenseStatus: license.status,
    };

    private readonly handleMouseUp = (e: MouseEvent) => {
        if (e.button === 3 || e.button === 4) {
            // Prevent mouse navigation buttons!
            e.preventDefault();
        }
    };
    private unsubscribe?: () => void;
    private unsubscribeLicense?: () => void;

    public componentDidMount() {
        if (isRecorder()) {
            // needed, since we have no padding in recorder
            document.body.className = "overflow-hidden";
            // prevent recorder-iframe from navigate back
            window.addEventListener("mouseup", this.handleMouseUp);
            // also catch "pointerup" from D3 in PanAndBrushContainer
            window.addEventListener("pointerup", this.handleMouseUp);
        }

        this.unsubscribe = session.subscribe({
            expired: (reason) => {
                if (reason === EExpireReasons.LoggedOut) {
                    this.forceUpdate(); // just rerender for title once user logged out
                }
            },
        });
        this.unsubscribeLicense = license.subscribe({
            licenseChanged: (licenseStatus) => this.setState({licenseStatus}),
        });
        if (!session.hasSession) {
            void license.retrieveLicense();
        }
    }

    public componentWillUnmount() {
        this.unsubscribe?.();
        this.unsubscribeLicense?.();
        if (isRecorder()) {
            window.removeEventListener("mouseup", this.handleMouseUp);
            window.removeEventListener("pointerup", this.handleMouseUp);
        }
    }

    @boundMethod
    public onReady() {
        this.setState({started: true});
    }

    public getTitle() {
        return "SEPView 7";
    }

    // <Titled> shall also be there if we are in startup
    public render() {
        return (
            <Titled title={this.getTitle}>
                <BrowserRouter>
                    <div className="App">{this.renderApp()}</div>
                </BrowserRouter>
            </Titled>
        );
    }

    // <Provider> only usefull after successfull startup and loading localstorage
    private renderApp() {
        if (!this.state.started) {
            return <Startup onReady={this.onReady} />;
        }

        const noHeaderFooter = !isRecorder();

        return (
            <Provider>
                {noHeaderFooter && <NavBar />}

                {noHeaderFooter && <Warnings />}

                <Route render={this.renderContent} />

                <SessionCheck>
                    <Copy2ClipboardModal />
                    <LoadingIndicator />
                </SessionCheck>

                {noHeaderFooter && <Footer />}
            </Provider>
        );
    }

    @boundMethod
    private renderContent() {
        const content = session.hasSession
            ? this.renderAuthenticated()
            : this.renderAnonymous();

        return <BlockWarning>{content}</BlockWarning>;
    }

    private renderAnonymous() {
        return (
            <Switch>
                <Route path="/eula" component={Eula} />
                <Route path="/login" render={this.renderLogin} />
                <Route path="/third-party" component={ThirdParty} />

                <Route component={createRedirector("/login")} />
            </Switch>
        );
    }

    private renderAuthenticated() {
        return (
            <Switch>
                <Route path="/eula" component={Eula} />
                <Route path="/login" render={this.redirectAfterLogin} />
                <Route path="/third-party" component={ThirdParty} />

                <Route component={Home} />
            </Switch>
        );
    }

    @boundMethod
    private renderLogin(props: RouteComponentProps) {
        const onAuthenticated = () =>
            props.history.replace(this.redirect(props));

        return (
            <ContainerCol col={4}>
                <div className="bg-focus rounded">
                    <IntlContext.Consumer children={this.renderLogo} />
                    <hr className="m-0 pb-3" />
                    <div className="pl-3 pr-3">
                        <Login onAuthenticated={onAuthenticated} />
                    </div>
                    <div
                        className="text-center pb-2"
                        style={{fontSize: "0.7rem"}}
                    >
                        <IntlContext.Consumer
                            children={this.renderLicenseStatus}
                        />
                    </div>
                </div>
            </ContainerCol>
        );
    }

    @boundMethod
    private renderLicenseStatus(intl: IntlShape) {
        const {licenseStatus} = this.state;

        if (!licenseStatus.expiry) {
            return null;
        }

        if (licenseStatus.isUnlimited) {
            return null;
        }

        const licenseText =
            licenseStatus.daysLeft <= 0
                ? intl2Str(intl, "Your license is expired.")
                : intl2Str(
                      intl,
                      "License valid for {daysLeft} days until {expiryDate, date}",
                      {
                          daysLeft: Math.max(0, licenseStatus.daysLeft),
                          expiryDate: parseISO(licenseStatus.expiry),
                      },
                  );
        return licenseText;
    }

    @boundMethod
    private renderLogo(intl: IntlShape) {
        return (
            <img
                className="w-100"
                alt={intl2Str(intl, "SEPView 7 logo")}
                src={sepview7logo}
            />
        );
    }

    private redirect(props: RouteComponentProps) {
        return (
            new URLSearchParams(props.location.search).get("redirect") ?? "/"
        );
    }

    @boundMethod
    private redirectAfterLogin(props: RouteComponentProps) {
        const redirect = this.redirect(props);
        return <Redirect to={redirect} push={true} />;
    }
}

export default App;
